// src/components/FeatureGate.jsx  v5
//
// Architecture:
//   ┌─ wrapper (position:relative) ──────────────────────────────┐
//   │  ┌─ children layer (blur + opacity reduced) ────────────┐  │
//   │  │  Real chart — visible as atmospheric background      │  │
//   │  └───────────────────────────────────────────────────────┘  │
//   │  ┌─ overlay (position:absolute, inset:0) ─────────────────┐  │
//   │  │  • soft radial + edge vignette (not black slabs)       │  │
//   │  │  • centered LockCard                                   │  │
//   │  └───────────────────────────────────────────────────────┘  │
//   └────────────────────────────────────────────────────────────┘
//
// v5 changes vs v4:
//   - Vignette is now a soft radial mask — not harsh black top/bottom slabs
//   - LockCard: tighter proportions, no gap waste, icon in a lit ring
//   - Plan badge: readable size (10px), refined pill
//   - CTA button: always full gold fill (not ghost until hover) — premium default
//   - Shimmer scan line on hover (single subtle pass, not looping noise)
//   - Arrow icon replaces StarSVG in CTA — more directional, premium feel
//   - blurAmount default raised to 7, childOpacity default raised to 0.45
//     so the teaser content reads more desirably underneath

import { useState, useEffect, useRef } from 'react';

/* ─── helpers ──────────────────────────────────────────── */
export function planCanAccess(userPlan = 'free', requiredPlans = []) {
  if (!requiredPlans || requiredPlans.length === 0) return true;
  return requiredPlans.includes(userPlan);
}

/* ─── plan meta ────────────────────────────────────────── */
const PLAN = {
  paid: {
    cssColor:     'var(--gold)',
    cssColorRgb:  '201,168,76',
    cssBg:        'rgba(201,168,76,.08)',
    cssBorder:    'rgba(201,168,76,.30)',
    cssBorderHov: 'rgba(201,168,76,.60)',
    label:        'Pro',
    btnBg:        'linear-gradient(135deg, #D4A843 0%, #B8891F 100%)',
    btnBgHov:     'linear-gradient(135deg, #E0B84E 0%, #C4951F 100%)',
    btnColor:     '#0A0A0C',
    glow:         '0 8px 32px rgba(201,168,76,.35), 0 2px 8px rgba(201,168,76,.20)',
    glowSm:       '0 4px 16px rgba(201,168,76,.25)',
    ringColor:    'rgba(201,168,76,.18)',
  },
  lifetime: {
    cssColor:     'var(--green)',
    cssColorRgb:  '46,204,138',
    cssBg:        'rgba(46,204,138,.07)',
    cssBorder:    'rgba(46,204,138,.28)',
    cssBorderHov: 'rgba(46,204,138,.55)',
    label:        'Lifetime',
    btnBg:        'linear-gradient(135deg, #2ECC8A 0%, #1E8A4E 100%)',
    btnBgHov:     'linear-gradient(135deg, #3EDDA0 0%, #249A5A 100%)',
    btnColor:     '#0A0A0C',
    glow:         '0 8px 32px rgba(46,204,138,.28), 0 2px 8px rgba(46,204,138,.15)',
    glowSm:       '0 4px 16px rgba(46,204,138,.20)',
    ringColor:    'rgba(46,204,138,.15)',
  },
};

/* ─── copy registry ────────────────────────────────────── */
const AUTO_COPY = {
  recovery:   { h: 'Unlock Your Recovery Plan',             d: 'Get a step-by-step plan to fix your biggest profit leak and project the expected impact.',   cta: 'Unlock Recovery Plan'    },
  benchmarks: { h: 'See Full Performance Benchmarks',       d: 'Compare your stats against top-performing traders across every key metric.',               cta: 'Unlock Benchmarks'       },
  whatif_scenarios: { h: 'See All What-If Scenarios',       d: 'Explore every simulated outcome, not just the first two.',                                  cta: 'Unlock All Scenarios'    },
  insights_full: { h: 'Unlock All Insights',                d: 'See every AI-detected insight, not just the top few.',                                       cta: 'Unlock All Insights'     },
  unlimited_reports: { h: 'Unlimited AI Reports',           d: 'Generate fresh coaching reports anytime, with no cooldown wait.',                            cta: 'Upgrade for Unlimited Reports' },
  compound:   { h: 'Forecast Your 12-Month Growth',         d: 'Project your balance forward using your personal edge — not generic averages.',           cta: 'See Your Projection'     },
  timing:     { h: 'Find Your Most Profitable Hours',       d: 'Pinpoint the 2-hour window draining your account — and the one printing money.',           cta: 'Reveal Your Best Hours'  },
  heatmap:    { h: 'Find Your Most Profitable Hours',       d: 'Pinpoint the 2-hour window draining your account — and the one printing money.',           cta: 'Reveal Your Best Hours'  },
  setup:      { h: 'Rank Every Setup by True Edge',         d: 'See which pairs, sessions, and patterns actually produce positive expectancy.',             cta: 'See Your Top 3 Setups'   },
  sizing:     { h: 'Optimize Your Position Sizing',         d: 'Calculate exactly how much extra profit optimal risk per trade would have generated.',      cta: 'See Sizing Impact'       },
  risk:       { h: 'Optimize Your Position Sizing',         d: 'Calculate exactly how much extra profit optimal risk per trade would have generated.',      cta: 'See Sizing Impact'       },
  emotion:    { h: 'Emotion vs. Performance',               d: 'See a direct correlation between your emotional state and your P&L across all trades.',     cta: 'See Emotional Impact'    },
  psychology: { h: 'Emotion vs. Performance',               d: 'See a direct correlation between your emotional state and your P&L across all trades.',     cta: 'See Emotional Impact'    },
  dna:        { h: 'Unlock Your Trader DNA Profile',        d: 'Understand the hidden behavioral patterns that define your edge — and your blind spots.',   cta: 'Reveal Your Profile'     },
  ai_dna_profile: { h: 'Unlock Your Trader DNA Profile',     d: 'Understand the hidden behavioral patterns that define your edge — and your blind spots.',   cta: 'Reveal Your Profile'     },
  milestone:  { h: 'Set & Track Performance Targets',       d: 'Define monthly P&L goals and watch your progress update with every trade you log.',         cta: 'Set Your Milestones'     },
  insight:    { h: 'AI-Powered Pattern Detection',          d: 'Get early warnings before recurring mistakes repeat and erode your monthly returns.',       cta: 'Unlock AI Insights'      },
};

function getAutoCopy(featureKey = '', label = 'Premium Feature', planLabel = 'Pro') {
  const fk = featureKey.toLowerCase();
  for (const [k, v] of Object.entries(AUTO_COPY)) {
    if (fk.includes(k)) return v;
  }
  return {
    h:   `Unlock ${label}`,
    d:   `Available on the ${planLabel} plan. Upgrade to access this feature.`,
    cta: `Upgrade to ${planLabel}`,
  };
}

/* ─── sub-components ───────────────────────────────────── */

/** Slim lock icon — clean stroke, no filled body */
function LockIcon({ color, size = 18, hovered }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      style={{
        display: 'block',
        transition: 'transform .35s cubic-bezier(.34,1.56,.64,1)',
        transform: hovered ? 'scale(1.12) translateY(-1px)' : 'scale(1)',
      }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2.5"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

/** Arrow right — directional CTA feels more premium than a star */
function ArrowIcon({ color, size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16"
      fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ display:'block', flexShrink:0 }}>
      <path d="M3 8h10M9 4l4 4-4 4"/>
    </svg>
  );
}

/** Plan badge — readable size, refined spacing */
function PlanBadge({ plan }) {
  const m = PLAN[plan] || PLAN.paid;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 11px',
      borderRadius: 'var(--radius-pill, 999px)',
      fontSize: 10, fontWeight: 700,
      letterSpacing: '.09em',
      textTransform: 'uppercase',
      background: m.cssBg,
      border: `1px solid ${m.cssBorder}`,
      color: m.cssColor,
      flexShrink: 0,
      fontFamily: 'var(--font-ui)',
    }}>
      {/* Diamond dot */}
      <span style={{
        width: 5, height: 5,
        background: m.cssColor,
        borderRadius: 1,
        transform: 'rotate(45deg)',
        display: 'inline-block',
        flexShrink: 0,
        opacity: .85,
      }}/>
      {m.label}
    </span>
  );
}

/* ─── shimmer scan — single pass on hover, then stops ─── */
function ShimmerScan({ active, colorRgb }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0, bottom: 0,
        width: '55%',
        background: `linear-gradient(105deg,
          transparent 0%,
          rgba(${colorRgb},.04) 40%,
          rgba(${colorRgb},.09) 50%,
          rgba(${colorRgb},.04) 60%,
          transparent 100%
        )`,
        transform: active ? 'translateX(240%)' : 'translateX(-30%)',
        transition: active
          ? 'transform .75s cubic-bezier(.4,0,.2,1)'
          : 'none',
      }}/>
    </div>
  );
}

/* ─── the lock card ────────────────────────────────────── */
function LockCard({ meta, unlockedBy, headline, description, ctaLabel, onUpgrade, hovered }) {
  const [btnHov, setBtnHov] = useState(false);

  return (
    <div style={{
      position: 'relative',
      background: 'rgba(12,11,9,.92)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: `1px solid ${hovered ? meta.cssBorderHov : meta.cssBorder}`,
      borderRadius: 'var(--radius, 12px)',
      padding: '22px 24px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      maxWidth: 320,
      width: '90%',
      overflow: 'hidden',
      boxShadow: hovered
        ? `${meta.glow}, inset 0 1px 0 rgba(255,255,255,.06)`
        : `${meta.glowSm}, inset 0 1px 0 rgba(255,255,255,.04)`,
      transition: 'border-color .25s, box-shadow .25s',
    }}>

      {/* Shimmer scan on hover */}
      <ShimmerScan active={hovered} colorRgb={meta.cssColorRgb}/>

      {/* Top radial glow — atmosphere, not decoration */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: -40, left: '50%',
        transform: 'translateX(-50%)',
        width: 220, height: 120,
        background: `radial-gradient(ellipse at 50% 0%, rgba(${meta.cssColorRgb},.14) 0%, transparent 70%)`,
        pointerEvents: 'none',
        transition: 'opacity .3s',
        opacity: hovered ? 1 : 0.6,
      }}/>

      {/* Icon ring */}
      <div style={{
        position: 'relative',
        width: 44, height: 44,
        borderRadius: '50%',
        background: `radial-gradient(circle, rgba(${meta.cssColorRgb},.12) 0%, rgba(${meta.cssColorRgb},.04) 100%)`,
        border: `1px solid ${meta.cssBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16,
        flexShrink: 0,
        transition: 'border-color .25s, background .25s',
        ...(hovered ? { borderColor: meta.cssBorderHov } : {}),
      }}>
        <LockIcon color={meta.cssColor} size={18} hovered={hovered}/>
      </div>

      {/* Headline */}
      <div style={{
        position: 'relative',
        fontSize: 15,
        fontWeight: 700,
        color: 'rgba(240,234,214,.95)',
        lineHeight: 1.3,
        marginBottom: 8,
        fontFamily: 'var(--font-ui)',
        letterSpacing: '-.01em',
      }}>
        {headline}
      </div>

      {/* Description */}
      <div style={{
        position: 'relative',
        fontSize: 'var(--text-sm, 12px)',
        color: 'rgba(200,192,168,.60)',
        lineHeight: 1.65,
        marginBottom: 16,
        fontFamily: 'var(--font-ui)',
        maxWidth: 250,
      }}>
        {description}
      </div>

      {/* Plan badges */}
      <div style={{
        position: 'relative',
        display: 'flex', gap: 6, flexWrap: 'wrap',
        justifyContent: 'center',
        marginBottom: 18,
      }}>
        {unlockedBy.map(p => <PlanBadge key={p} plan={p}/>)}
      </div>

      {/* CTA — always filled gold, lifts on hover */}
      <button
        onMouseEnter={() => setBtnHov(true)}
        onMouseLeave={() => setBtnHov(false)}
        onClick={e => { e.stopPropagation(); onUpgrade?.(); }}
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          gap: 8,
          padding: '10px 22px',
          borderRadius: 'var(--radius-sm, 8px)',
          background: btnHov ? meta.btnBgHov : meta.btnBg,
          border: 'none',
          color: meta.btnColor,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '.07em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          fontFamily: 'var(--font-ui)',
          whiteSpace: 'nowrap',
          minWidth: 168,
          transform: btnHov ? 'translateY(-2px)' : 'translateY(0)',
          boxShadow: btnHov
            ? `0 8px 24px rgba(${meta.cssColorRgb},.40)`
            : `0 4px 12px rgba(${meta.cssColorRgb},.22)`,
          transition: 'transform .2s cubic-bezier(.4,0,.2,1), box-shadow .2s, background .15s',
        }}
      >
        {ctaLabel}
        <ArrowIcon color={meta.btnColor} size={12}/>
      </button>
    </div>
  );
}

/* ─── main export ──────────────────────────────────────── */
/**
 * FeatureGate v5
 *
 * Props
 * ──────────────────────────────────────────────────────────
 * userPlan       "free" | "paid" | "lifetime"
 * requiredPlans  string[]
 * featureKey     string   — selects auto headline + description + CTA
 * label          string   — feature name (fallback for auto-copy)
 * headline       string   — override the auto headline
 * description    string   — override the auto description
 * ctaLabel       string   — override the auto CTA button label
 * upgradeHref    string   — fallback nav if onUpgrade not provided
 * onUpgrade      function — called when CTA is clicked
 * blurAmount     number   — px blur on children (default 7)
 * childOpacity   number   — opacity of blurred children (default 0.45)
 * wrapperStyle   object   — extra style on outer wrapper
 * children       ReactNode — the REAL chart / content to blur behind the gate
 */
export default function FeatureGate({
  userPlan      = 'free',
  requiredPlans = ['paid', 'lifetime'],
  featureKey    = '',
  label         = 'Premium Feature',
  headline,
  description,
  ctaLabel,
  upgradeHref   = '/pricing',
  onUpgrade,
  blurAmount    = 7,
  childOpacity  = 0.45,
  minHeight     = 300,
  wrapperStyle,
  children,
}) {
  const [hovered, setHovered] = useState(false);
  const isLocked = !planCanAccess(userPlan, requiredPlans);

  if (!isLocked) return <>{children}</>;

  const unlockedBy  = requiredPlans.length ? requiredPlans : ['paid'];
  const primaryPlan = unlockedBy.includes('paid') ? 'paid' : unlockedBy[0];
  const meta        = PLAN[primaryPlan] || PLAN.paid;
  const copy        = getAutoCopy(featureKey, label, meta.label);
  const displayH    = headline    || copy.h;
  const displayD    = description || copy.d;
  const displayCTA  = ctaLabel    || copy.cta;

  // LockCard needs roughly ~290px to lay out icon + headline + description +
  // plan badges + CTA without crowding. Never let the wrapper shrink below
  // that, no matter how small the (blurred) content behind it is — this is
  // what previously caused the lock card to spill out of its box and
  // overlap neighboring sections.
  const safeMinHeight = Math.max(minHeight, 340);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', minHeight: safeMinHeight, overflow: 'hidden', borderRadius: 'var(--radius, 12px)', ...wrapperStyle }}
    >
      {/* ── Layer 1: teaser content — blurred but readable ── */}
      <div
        aria-hidden="true"
        style={{
          filter: `blur(${blurAmount}px)`,
          opacity: childOpacity,
          pointerEvents: 'none',
          userSelect: 'none',
          transform: 'scale(1.04)',
          transformOrigin: 'center center',
          width: '100%',
          minHeight: safeMinHeight,
          transition: 'opacity .3s',
        }}
      >
        {children}
      </div>

      {/* ── Layer 2: soft vignette — not black slabs ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          borderRadius: 'var(--radius, 12px)',
          // Soft radial vignette from centre outward — lets the teaser
          // breathe while keeping the card readable
          background: `
            radial-gradient(
              ellipse 90% 80% at 50% 50%,
              transparent 0%,
              rgba(10,9,7,.55) 100%
            )
          `,
        }}
      />

      {/* ── Layer 3: lock card ── */}
      <div style={{
        position: 'absolute', inset: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 12px',
        boxSizing: 'border-box',
      }}>
        <LockCard
          meta={meta}
          unlockedBy={unlockedBy}
          headline={displayH}
          description={displayD}
          ctaLabel={displayCTA}
          onUpgrade={onUpgrade ?? (() => { window.location.href = upgradeHref; })}
          hovered={hovered}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   IntelligenceGate
   ─────────────────────────────────────────────────────────────────────────────
   Alternative gate pattern for "70% visible, 30% locked" embedded paywall.

   Architecture:
     ┌─ wrapper ───────────────────────────────────────────────────────────────┐
     │  children rendered at full opacity (no blur on the whole block)         │
     │                                                                          │
     │  ┌─ fade-mask over the BOTTOM ~35% ──────────────────────────────────┐  │
     │  │  Linear gradient: transparent → surface-color                     │  │
     │  └────────────────────────────────────────────────────────────────────┘  │
     │                                                                          │
     │  ┌─ inline CTA strip (position:absolute, bottom:0) ──────────────────┐  │
     │  │  ◈ Pro  ·  headline  ·  [Unlock button]                           │  │
     │  └────────────────────────────────────────────────────────────────────┘  │
     └──────────────────────────────────────────────────────────────────────────┘

   Props — same as FeatureGate, plus:
     revealHeight   number   — px of content visible before fade (default 220)
     surfaceColor   string   — background color of the card/panel (default var(--dark2))
     ctaSubline     string   — small text under the CTA button (e.g. "4 AI recommendations hidden")

   Usage:
     <IntelligenceGate {...gate('whatif_compound')} revealHeight={260} ctaSubline="12-month projection hidden">
       <CompoundProjectorTeaser ... />
     </IntelligenceGate>
     When the user has access, renders children unwrapped.
─────────────────────────────────────────────────────────────────────────────── */
export function IntelligenceGate({
  userPlan      = 'free',
  requiredPlans = ['paid', 'lifetime'],
  featureKey    = '',
  label         = 'Premium Feature',
  headline,
  ctaLabel,
  ctaSubline,
  upgradeHref   = '/pricing',
  onUpgrade,
  revealHeight  = 220,
  surfaceColor  = 'var(--dark2, #111113)',
  wrapperStyle,
  children,
}) {
  const [btnHov, setBtnHov] = useState(false);
  const isLocked = !planCanAccess(userPlan, requiredPlans);

  if (!isLocked) return <>{children}</>;

  const unlockedBy  = requiredPlans.length ? requiredPlans : ['paid'];
  const primaryPlan = unlockedBy.includes('paid') ? 'paid' : unlockedBy[0];
  const meta        = PLAN[primaryPlan] || PLAN.paid;
  const copy        = getAutoCopy(featureKey, label, meta.label);
  const displayH    = headline || copy.h;
  const displayCTA  = ctaLabel || copy.cta;

  const handleUpgrade = onUpgrade ?? (() => { window.location.href = upgradeHref; });

  return (
    <div style={{ position: 'relative', overflow: 'hidden', maxHeight: revealHeight + 80, ...wrapperStyle }}>

      {/* ── Real teaser content — fully visible, no blur ── */}
      <div style={{
        maxHeight: revealHeight,
        overflow: 'hidden',
        maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
        {children}
      </div>

      {/* ── Inline CTA strip — fades up from the bottom ── */}
      <div style={{
        position: 'relative',
        marginTop: -32,
        background: `linear-gradient(to bottom, transparent 0%, ${surfaceColor} 28%)`,
        paddingTop: 40,
        paddingBottom: 16,
        paddingLeft: 20,
        paddingRight: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>

        {/* Left: badge + headline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* Glow dot + plan badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px 3px 7px',
            background: meta.cssBg,
            border: `1px solid ${meta.cssBorder}`,
            borderRadius: 'var(--radius-pill, 999px)',
            flexShrink: 0,
          }}>
            <span style={{
              width: 6, height: 6,
              background: meta.cssColor,
              borderRadius: '50%',
              display: 'inline-block',
              boxShadow: `0 0 6px rgba(${meta.cssColorRgb}, .7)`,
              flexShrink: 0,
            }}/>
            <span style={{
              fontSize: 9, fontWeight: 800,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: meta.cssColor,
              fontFamily: 'var(--font-ui)',
            }}>
              {meta.label}
            </span>
          </div>

          {/* Headline */}
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'rgba(240,234,214,.80)',
            fontFamily: 'var(--font-ui)',
            letterSpacing: '-.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {displayH}
          </span>
        </div>

        {/* Right: CTA + subline */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <button
            onMouseEnter={() => setBtnHov(true)}
            onMouseLeave={() => setBtnHov(false)}
            onClick={handleUpgrade}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '8px 18px',
              borderRadius: 'var(--radius-sm, 8px)',
              background: btnHov ? meta.btnBgHov : meta.btnBg,
              border: 'none',
              color: meta.btnColor,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '.07em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              whiteSpace: 'nowrap',
              transform: btnHov ? 'translateY(-1px)' : 'translateY(0)',
              boxShadow: btnHov
                ? `0 6px 20px rgba(${meta.cssColorRgb},.38)`
                : `0 3px 10px rgba(${meta.cssColorRgb},.20)`,
              transition: 'transform .18s, box-shadow .18s, background .12s',
            }}
          >
            {displayCTA}
            <ArrowIcon color={meta.btnColor} size={11}/>
          </button>
          {ctaSubline && (
            <span style={{
              fontSize: 9,
              color: `rgba(${meta.cssColorRgb}, .50)`,
              fontFamily: 'var(--font-ui)',
              letterSpacing: '.03em',
            }}>
              {ctaSubline}
            </span>
          )}
        </div>

      </div>
    </div>
  );
}